import { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading
  const [impersonating, setImpersonating] = useState(false);
  const [impersonator, setImpersonator] = useState(null);
  const [canImpersonate, setCanImpersonate] = useState(false);
  const [idleMinutes, setIdleMinutes] = useState(0);

  const applyMe = (data) => {
    setUser(data.user);
    setImpersonating(!!data.impersonating);
    setImpersonator(data.impersonator || null);
    setCanImpersonate(!!data.can_impersonate);
    setIdleMinutes(data.session_idle_minutes || 0);
  };

  const refreshMe = () =>
    api.get('/auth/me').then(r => applyMe(r.data)).catch(() => setUser(null));

  useEffect(() => { refreshMe(); }, []);

  const login = async (email, password) => {
    const r = await api.post('/auth/login', { email, password });
    if (r.data.user) { await refreshMe(); return { ok: true }; }
    return r.data; // { mfa_required } or { mfa_setup_required, qr, secret, email }
  };

  const completeMfa = async (code) => {
    const r = await api.post('/auth/mfa/verify', { code });
    if (r.data.user) { await refreshMe(); return { ok: true }; }
    return r.data;
  };

  const logout = async () => {
    await api.post('/auth/logout');
    setUser(null); setImpersonating(false); setImpersonator(null); setCanImpersonate(false);
  };

  const impersonate = async (userId) => {
    await api.post(`/auth/impersonate/${userId}`);
    await refreshMe();
  };

  const stopImpersonating = async () => {
    await api.post('/auth/impersonate/stop');
    // The admin's login session was never touched; the caller reloads, which
    // re-fetches /auth/me cleanly. We deliberately do NOT call refreshMe here
    // (a fetch in the transition window was causing false logouts).
  };

  return (
    <AuthContext.Provider value={{
      user, login, logout, setUser, refreshMe, completeMfa, idleMinutes,
      impersonating, impersonator, canImpersonate, impersonate, stopImpersonating,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function isTTC(user) {
  return user && ['ttc_superadmin', 'ttc_staff'].includes(user.role);
}

export function isCustomerAdmin(user) {
  return user && ['ttc_superadmin', 'ttc_staff', 'customer_admin'].includes(user.role);
}
